import {
  BanknotesIcon,
  CurrencyPoundIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import PriceFormat from "../../utils/priceFormat";
import classNames from "../../utils/classNames";
import { IVehicle } from "../../models/Vehicle";

interface VehicleFinancialStatsProps {
  vehicle: IVehicle;
}

// Consistent em-dash fallback so empty analytics don't masquerade as £0.00.
const money = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : PriceFormat(v);

const count = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : v.toLocaleString("en-GB");

interface StatBlockProps {
  label: string;
  value: string;
  tone?: "neutral" | "emerald" | "rose" | "primary";
  caption?: string;
}

const TONE_CLASSES: Record<NonNullable<StatBlockProps["tone"]>, string> = {
  neutral: "bg-white text-gray-900 ring-gray-200",
  emerald: "bg-emerald-50 text-emerald-900 ring-emerald-200",
  rose: "bg-rose-50 text-rose-900 ring-rose-200",
  primary: "bg-primary-50 text-primary-900 ring-primary-200",
};

const StatBlock = ({ label, value, tone = "neutral", caption }: StatBlockProps) => (
  <div
    className={classNames(
      "rounded-lg px-4 py-3 ring-1 ring-inset",
      TONE_CLASSES[tone]
    )}
  >
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="mt-1 text-xl font-semibold leading-tight">{value}</p>
    {caption && <p className="mt-0.5 text-xs text-gray-500">{caption}</p>}
  </div>
);

// Mirrors the financial breakdown surfaced in the awaiting-inventory row's
// Wilma Intelligence cell, but at page-width so the headline numbers can
// breathe. Sourced entirely from `IVehicle` fields that the partial DTO
// already populates — no extra requests.
export default function VehicleFinancialStats({ vehicle }: VehicleFinancialStatsProps) {
  // Export value fallback: sum the accepted/pending engine + VFE offers
  // already on the partial DTO, so the block is meaningful even before the
  // dedicated `exportValue` field is populated upstream.
  const parseOfferPrice = (raw: string | null | undefined) => {
    if (raw === null || raw === undefined || raw === "") return null;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  };
  const engineOffer = parseOfferPrice(vehicle.enginesWorldwideEngineOffer);
  const vfeOffer = parseOfferPrice(vehicle.enginesWorldwideVfeOffer);
  const exportFallback =
    engineOffer !== null || vfeOffer !== null
      ? (engineOffer ?? 0) + (vfeOffer ?? 0)
      : null;

  const engineValue = vehicle.engineValue ?? vehicle.ebayEnginePrice ?? null;
  const gearboxValue = vehicle.gearboxValue ?? null;
  const exportValue = vehicle.exportValue ?? exportFallback;
  const partsInStock =
    vehicle.totalPartsInStock ?? vehicle.availablePartCount ?? null;

  return (
    <Card title="Financial Stats" titleClassName="border-b border-gray-100">
      <div className="px-4 py-5 sm:px-6 flex flex-col gap-5">
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <BanknotesIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            Parts value
          </h3>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatBlock
              label="Total Parts Value"
              value={money(vehicle.totalValueAllParts)}
              caption="Across every part on the donor"
            />
            <StatBlock
              label="Projected Sales"
              value={money(vehicle.projectedSales)}
              tone="emerald"
              caption="Wilma forecast at current prices"
            />
            <StatBlock
              label="Value In Stock"
              value={money(vehicle.valuePartsInStock)}
              caption="Unsold stock still on shelf"
            />
            <StatBlock
              label="Parts In Stock"
              value={count(partsInStock)}
              caption="Available part count"
            />
          </div>
        </section>

        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <TruckIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            Component values
          </h3>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatBlock label="Engine" value={money(engineValue)} />
            <StatBlock label="Gearbox" value={money(gearboxValue)} />
            <StatBlock label="Export" value={money(exportValue)} />
          </div>
        </section>

        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <CurrencyPoundIcon
              className="h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
            Buy-side
          </h3>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <StatBlock
              label="Salvage"
              value={money(vehicle.valuationTradePoor)}
              caption="Poor-trade valuation baseline"
            />
            <StatBlock
              label="Recommended Purchase"
              value={money(vehicle.recommendedPurchasePrice)}
              tone="primary"
              caption="Wilma's suggested buy ceiling"
            />
          </div>
        </section>
      </div>
    </Card>
  );
}
